import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import store from '../../../../state';
import { appendAuthToken } from '../../../../config';
import authService from '../../../../services/AuthService';
import { TropicalSummaryPayload, TropicalStormSummary, TropicalDisturbance } from '../../../../types';
import { poiModal } from '../../PoiModal';

interface ProcessedStorm {
  summary: TropicalStormSummary;
  distanceMiles: number;
  bearing: string;
}

let cachedTropicalSummary: TropicalSummaryPayload | null = null;
let lastTropicalFetchTime = 0;
let userSelectedShowAll = false;

function haversineDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getBearingLabel(lat1: number, lon1: number, lat2: number, lon2: number): string {
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const idx = Math.floor((bearing + 11.25) / 22.5) % 16;
  return directions[idx];
}

function getCategoryColor(badge: string): string {
  switch (badge) {
    case 'Cat 5':
      return '#581c87';
    case 'Cat 4':
      return '#b91c1c';
    case 'Cat 3':
      return '#ea580c';
    case 'Cat 2':
      return '#d97706';
    case 'Cat 1':
      return '#ca8a04';
    case 'TS':
      return '#0284c7';
    case 'TD':
      return '#06b6d4';
    default:
      return '#64748b';
  }
}

export class TropicalStormsWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Hurricanes & Tropical Storms';
    const containerId = `poi-tropical-card-${widget.id}`;

    if (cachedTropicalSummary) {
      return `
        <div id="${containerId}" class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
          ${this.buildCardContent(title, cachedTropicalSummary, ctx.lat, ctx.lon, userSelectedShowAll)}
        </div>
      `;
    }

    return `
      <div id="${containerId}" class="metric-card" style="background: linear-gradient(145deg, #13151f 0%, #0d0e15 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🌀 ${title}</span>
          <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(2, 132, 199, 0.15); color: #38bdf8; border-radius: 999px; font-weight: 600;">NOAA NHC Live</span>
        </div>
        <div style="display: flex; align-items: center; justify-content: center; padding: 24px; color: #94a3b8; font-size: 0.85rem; gap: 8px;">
          <span>Checking active tropical cyclones & disturbances...</span>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const containerId = `poi-tropical-card-${widget.id}`;
    const containerEl = document.getElementById(containerId);
    if (!containerEl) return;

    const title = widget.title || 'Hurricanes & Tropical Storms';

    const now = Date.now();
    if (cachedTropicalSummary && now - lastTropicalFetchTime < 5 * 60 * 1000) {
      containerEl.innerHTML = this.buildCardContent(title, cachedTropicalSummary, ctx.lat, ctx.lon, userSelectedShowAll);
      this.bindActions(containerEl, title, ctx.lat, ctx.lon);
      return;
    }

    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const dataUrl = `${apiServer}${tileRoot}/observed/tropical/tropical_summary.json`;
    const fetchUrl = appendAuthToken(dataUrl);
    const token = authService.getSessionToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(fetchUrl, {
      headers,
      credentials: fetchUrl.includes('auth.skyskout.com') ? 'include' : 'same-origin'
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((summary: TropicalSummaryPayload) => {
        cachedTropicalSummary = summary;
        lastTropicalFetchTime = Date.now();
        containerEl.innerHTML = this.buildCardContent(title, summary, ctx.lat, ctx.lon, userSelectedShowAll);
        this.bindActions(containerEl, title, ctx.lat, ctx.lon);
      })
      .catch((err) => {
        console.warn('[TropicalStormsWidget] Failed to fetch tropical_summary.json, trying geojson fallback:', err);
        // Fallback to minimal summary from geojson if needed
        this.fetchGeoJsonFallback(apiServer, tileRoot, containerEl, title, ctx.lat, ctx.lon);
      });
  }

  private fetchGeoJsonFallback(
    apiServer: string,
    tileRoot: string,
    containerEl: HTMLElement,
    title: string,
    userLat: number,
    userLon: number
  ): void {
    const geojsonUrl = appendAuthToken(`${apiServer}${tileRoot}/observed/tropical/tropical_active.geojson`);
    const token = authService.getSessionToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    fetch(geojsonUrl, {
      headers,
      credentials: geojsonUrl.includes('auth.skyskout.com') ? 'include' : 'same-origin'
    })
      .then((r) => r.json())
      .then((fc: any) => {
        const stormsMap = new Map<string, TropicalStormSummary>();
        const disturbances: TropicalDisturbance[] = [];

        if (Array.isArray(fc.features)) {
          for (const feat of fc.features) {
            const p = feat.properties || {};
            if (p.feature_type === 'track_point' && p.tau_hr === 0) {
              const sid = p.storm_id || p.storm_name;
              if (sid && !stormsMap.has(sid)) {
                stormsMap.set(sid, {
                  id: sid,
                  name: p.storm_name || 'Active Storm',
                  classification: p.development || 'Tropical Storm',
                  category_badge: p.category_badge || 'TS',
                  category_desc: p.description || '',
                  intensity_kt: p.max_wind_kt || 0,
                  intensity_mph: p.max_wind_mph || Math.round((p.max_wind_kt || 0) * 1.15078),
                  pressure_mb: p.pressure_mb || 0,
                  current_lat: feat.geometry?.coordinates?.[1],
                  current_lon: feat.geometry?.coordinates?.[0]
                });
              }
            } else if (p.feature_type === 'genesis_area') {
              disturbances.push({
                basin: p.basin || 'Atlantic',
                area_id: p.area_id || 'Disturbance',
                prob_2day: p.prob_2day || '20%',
                risk_2day: p.risk_2day || 'Low',
                prob_7day: p.prob_7day || '30%',
                risk_7day: p.risk_7day || 'Low',
                formation_level: p.formation_level || 'Low'
              });
            }
          }
        }

        const fallbackPayload: TropicalSummaryPayload = {
          active_storms_count: stormsMap.size,
          active_storms: Array.from(stormsMap.values()),
          disturbances
        };

        cachedTropicalSummary = fallbackPayload;
        lastTropicalFetchTime = Date.now();
        containerEl.innerHTML = this.buildCardContent(title, fallbackPayload, userLat, userLon, userSelectedShowAll);
        this.bindActions(containerEl, title, userLat, userLon);
      })
      .catch((err2) => {
        console.warn('[TropicalStormsWidget] GeoJSON fallback failed:', err2);
        containerEl.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🌀 ${title}</span>
            <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(2, 132, 199, 0.15); color: #38bdf8; border-radius: 999px; font-weight: 600;">NOAA NHC</span>
          </div>
          <div style="text-align: center; padding: 18px; color: #94a3b8; font-size: 0.8rem;">
            Unable to load active tropical storm telemetry.
            <div style="margin-top: 8px;">
              <button class="gw-trop-open-map-btn" style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #e2e8f0; border-radius: 6px; padding: 6px 12px; font-size: 0.75rem; cursor: pointer;">
                Open Tropical Cyclones Map
              </button>
            </div>
          </div>
        `;
        this.bindActions(containerEl, title, userLat, userLon);
      });
  }

  private processStorms(summary: TropicalSummaryPayload, userLat: number, userLon: number): ProcessedStorm[] {
    if (!summary || !Array.isArray(summary.active_storms)) return [];

    const processed: ProcessedStorm[] = [];

    for (const storm of summary.active_storms) {
      const sLat = storm.current_lat;
      const sLon = storm.current_lon;

      let distMiles = 999999;
      let bearing = '';

      if (sLat !== undefined && sLon !== undefined && sLat !== null && sLon !== null) {
        distMiles = haversineDistanceMiles(userLat, userLon, sLat, sLon);
        bearing = getBearingLabel(userLat, userLon, sLat, sLon);
      }

      processed.push({
        summary: storm,
        distanceMiles: Math.round(distMiles),
        bearing
      });
    }

    return processed.sort((a, b) => a.distanceMiles - b.distanceMiles);
  }

  private buildCardContent(
    title: string,
    summary: TropicalSummaryPayload,
    userLat: number,
    userLon: number,
    showAll: boolean
  ): string {
    const allProcessed = this.processStorms(summary, userLat, userLon);
    const nearbyStorms = allProcessed.filter((p) => p.distanceMiles <= 1000);
    const displayedStorms = showAll ? allProcessed : nearbyStorms;

    const totalStormsCount = allProcessed.length;
    const disturbances = summary.disturbances || [];

    // Header toggle pills
    const toggleHtml = `
      <div style="display: flex; background: rgba(255, 255, 255, 0.05); padding: 2px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); font-size: 0.7rem; font-weight: 600;">
        <button class="gw-trop-toggle-btn ${!showAll ? 'active' : ''}" data-show-all="false" style="padding: 3px 8px; border: none; border-radius: 6px; cursor: pointer; background: ${!showAll ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${!showAll ? '#38bdf8' : '#94a3b8'}; transition: all 0.15s ease;">
          Within 1,000 mi
        </button>
        <button class="gw-trop-toggle-btn ${showAll ? 'active' : ''}" data-show-all="true" style="padding: 3px 8px; border: none; border-radius: 6px; cursor: pointer; background: ${showAll ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${showAll ? '#38bdf8' : '#94a3b8'}; transition: all 0.15s ease;">
          All Storms (${totalStormsCount})
        </button>
      </div>
    `;

    let bodyHtml = '';

    if (displayedStorms.length > 0) {
      bodyHtml = `
        <div style="display: flex; flex-direction: column; gap: 12px; margin-top: 12px;">
          ${displayedStorms.map((p) => this.renderStormCard(p)).join('')}
        </div>
      `;
    } else {
      // Reassuring all-clear state
      const globalNote =
        totalStormsCount > 0
          ? `<div style="font-size: 0.72rem; color: #38bdf8; margin-top: 4px;">
               ${totalStormsCount} active storm${totalStormsCount > 1 ? 's' : ''} currently monitored outside 1,000 mi.
             </div>`
          : '';

      let distHtml = '';
      if (disturbances.length > 0) {
        distHtml = `
          <div style="margin-top: 10px; width: 100%; border-top: 1px solid rgba(255, 255, 255, 0.06); padding-top: 10px; text-align: left;">
            <div style="font-size: 0.72rem; font-weight: 700; color: #cbd5e1; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">
              Active Disturbance Outlook (7-Day Genesis)
            </div>
            <div style="display: flex; flex-direction: column; gap: 6px;">
              ${disturbances
                .slice(0, 3)
                .map((d) => {
                  const riskColor = d.risk_7day?.toLowerCase() === 'high' ? '#ef4444' : d.risk_7day?.toLowerCase() === 'medium' ? '#fbbf24' : '#38bdf8';
                  return `
                    <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.05); padding: 6px 10px; border-radius: 6px; font-size: 0.72rem;">
                      <span style="color: #f1f5f9; font-weight: 600;">${this.escapeHtml(d.basin)}: ${this.escapeHtml(d.area_id)}</span>
                      <span style="color: ${riskColor}; font-weight: 700;">${this.escapeHtml(d.prob_7day || d.prob_2day)} Chance</span>
                    </div>
                  `;
                })
                .join('')}
            </div>
          </div>
        `;
      }

      bodyHtml = `
        <div style="background: rgba(2, 132, 199, 0.05); border: 1px solid rgba(2, 132, 199, 0.2); border-radius: 12px; padding: 18px 16px; margin-top: 10px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 6px;">
          <div style="font-size: 1.5rem;">🌊🛡️</div>
          <div style="font-size: 0.95rem; font-weight: 700; color: #38bdf8;">No Active Tropical Storms Within 1,000 Miles</div>
          <div style="font-size: 0.75rem; color: #94a3b8; max-width: 340px; line-height: 1.4;">
            No monitored tropical depressions, storms, or hurricanes currently threatening this forecast region.
          </div>
          ${globalNote}
          ${
            totalStormsCount > 0
              ? `
            <button class="gw-trop-toggle-btn" data-show-all="true" style="margin-top: 8px; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); color: #38bdf8; border-radius: 6px; padding: 5px 12px; font-size: 0.75rem; font-weight: 600; cursor: pointer;">
              Show All Monitored Storms (${totalStormsCount})
            </button>
          `
              : ''
          }
          ${distHtml}
        </div>
      `;
    }

    return `
      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span style="font-size: 1.1rem;">🌀</span>
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">${this.escapeHtml(title)}</span>
        </div>
        ${toggleHtml}
      </div>
      ${bodyHtml}
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 8px; border-top: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.72rem; color: #64748b;">
        <span>NOAA NHC & CPHC Authoritative Feed</span>
        <button class="gw-trop-open-map-btn" style="background: none; border: none; color: #38bdf8; font-weight: 600; cursor: pointer; padding: 0;">
          Open Tropical Layer →
        </button>
      </div>
    `;
  }

  private renderStormCard(p: ProcessedStorm): string {
    const s = p.summary;
    const catBadge = s.category_badge || 'TS';
    const catBg = getCategoryColor(catBadge);
    const windMph = s.intensity_mph || Math.round((s.intensity_kt || 0) * 1.15078);
    const pressureStr = s.pressure_mb && s.pressure_mb > 800 ? `${s.pressure_mb} mb` : 'Pressure N/A';

    let movementStr = '';
    if (s.movement_dir && s.movement_speed_kt) {
      const speedMph = Math.round(s.movement_speed_kt * 1.15078);
      movementStr = `Moving ${s.movement_dir} at ${speedMph} mph`;
    } else if (s.movement_dir) {
      movementStr = `Moving ${s.movement_dir}`;
    }

    const distStr = p.distanceMiles < 999999 ? `${p.distanceMiles.toLocaleString()} mi ${p.bearing}` : 'Location N/A';

    // 3-Day Forecast Trajectory Strip
    const forecast3Day = s.forecast_3day || [];
    let forecastStripHtml = '';

    if (forecast3Day.length > 0) {
      const steps = forecast3Day.slice(0, 3);
      forecastStripHtml = `
        <div style="margin-top: 10px; background: rgba(0, 0, 0, 0.25); border-radius: 8px; padding: 8px 10px;">
          <div style="font-size: 0.68rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;">
            3-Day Forecast Progression (Direction & Strength)
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(85px, 1fr)); gap: 6px;">
            ${steps
              .map((pt) => {
                const stepCatBadge = pt.category_badge || 'TS';
                const stepCatBg = getCategoryColor(stepCatBadge);
                const stepWindMph = pt.max_wind_mph || Math.round((pt.max_wind_kt || 0) * 1.15078);
                const stepDir = pt.movement_dir ? `Towards ${pt.movement_dir}` : '';
                const timeLbl = pt.valid_time_lbl || `+${pt.tau_hr}h`;

                return `
                  <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 8px; display: flex; flex-direction: column; gap: 3px; align-items: center; text-align: center;">
                    <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 600;">${this.escapeHtml(timeLbl)}</span>
                    <span style="font-size: 0.72rem; padding: 1px 6px; background: ${stepCatBg}; color: #ffffff; border-radius: 4px; font-weight: 800;">
                      ${this.escapeHtml(stepCatBadge)}
                    </span>
                    <span style="font-size: 0.72rem; color: #e2e8f0; font-weight: 700;">${stepWindMph} mph</span>
                    ${stepDir ? `<span style="font-size: 0.62rem; color: #38bdf8; font-weight: 600;">${this.escapeHtml(stepDir)}</span>` : ''}
                  </div>
                `;
              })
              .join('')}
          </div>
        </div>
      `;
    }

    return `
      <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 10px; padding: 12px; display: flex; flex-direction: column; gap: 8px;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 0.75rem; padding: 2px 7px; background: ${catBg}; color: #ffffff; border-radius: 4px; font-weight: 800; letter-spacing: 0.3px;">
              ${this.escapeHtml(catBadge)}
            </span>
            <span style="font-size: 0.95rem; font-weight: 700; color: #f8fafc;">
              ${this.escapeHtml(s.name)}
            </span>
          </div>
          <div style="font-size: 0.78rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.15); padding: 2px 8px; border-radius: 6px; white-space: nowrap;">
            ${distStr}
          </div>
        </div>

        <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.78rem; color: #94a3b8; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; gap: 8px; align-items: center;">
            <span style="color: #e2e8f0; font-weight: 700;">${windMph} mph winds</span>
            <span>•</span>
            <span>${pressureStr}</span>
          </div>
          ${movementStr ? `<div style="color: #cbd5e1; font-weight: 600;">${this.escapeHtml(movementStr)}</div>` : ''}
        </div>

        ${forecastStripHtml}

        <div style="display: flex; justify-content: flex-end; margin-top: 4px;">
          <button class="gw-trop-fly-btn" data-lat="${s.current_lat || 0}" data-lon="${s.current_lon || 0}" style="background: rgba(2, 132, 199, 0.15); border: 1px solid rgba(2, 132, 199, 0.35); color: #38bdf8; font-size: 0.74rem; font-weight: 600; padding: 5px 12px; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 4px; transition: all 0.15s ease;">
            <span>View Track & Cone on Map</span>
            <span>→</span>
          </button>
        </div>
      </div>
    `;
  }

  private bindActions(container: HTMLElement, title: string, userLat: number, userLon: number): void {
    // Toggle between 'within 1000mi' and 'all storms'
    container.querySelectorAll('.gw-trop-toggle-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const showAll = target.getAttribute('data-show-all') === 'true';
        userSelectedShowAll = showAll;

        if (cachedTropicalSummary) {
          container.innerHTML = this.buildCardContent(title, cachedTropicalSummary, userLat, userLon, showAll);
          this.bindActions(container, title, userLat, userLon);
        }
      });
    });

    // Fly to specific storm
    container.querySelectorAll('.gw-trop-fly-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const lat = parseFloat(target.getAttribute('data-lat') || '0');
        const lon = parseFloat(target.getAttribute('data-lon') || '0');

        store.update({ primaryProduct: 'tropical_cyclones', tropicalEnabled: true });

        if (typeof (window as any).flyToTropicalStorm === 'function') {
          (window as any).flyToTropicalStorm(lat, lon);
        } else if ((window as any).scoutMap) {
          (window as any).scoutMap.flyTo({ center: [lon, lat], zoom: 6.5, essential: true });
        }

        poiModal.close();
      });
    });

    // Open tropical cyclones map layer
    container.querySelectorAll('.gw-trop-open-map-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ primaryProduct: 'tropical_cyclones', tropicalEnabled: true });
        poiModal.close();
      });
    });
  }

  private escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
